/**
 * DashboardFilterBar
 *
 * Renders configured filters with appropriate controls based on filter type:
 * - date: Date picker
 * - number: Number input
 * - text: Text input
 * - singleselect: Searchable single-select dropdown
 * - multiselect: Searchable multi-select with autocomplete and search history
 *
 * Emits active filter values whenever any filter changes.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { format } from "date-fns";
import {
  CalendarIcon,
  X,
  Check,
  ChevronsUpDown,
  Filter,
  History,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import type { ConfiguredFilter } from "./FilterSuggestionsDialog";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ActiveFilterValues {
  [filterId: string]: unknown;
}

interface DashboardFilterBarProps {
  filters: ConfiguredFilter[];
  dashboardId: string;
  onChange: (values: ActiveFilterValues) => void;
}

// ─── Search History Helpers ───────────────────────────────────────────────────

const HISTORY_KEY_PREFIX = "filter-history:";
const MAX_HISTORY = 10;

function getSearchHistory(dashboardId: string, filterId: string): string[] {
  try {
    const raw = localStorage.getItem(`${HISTORY_KEY_PREFIX}${dashboardId}:${filterId}`);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function addToSearchHistory(dashboardId: string, filterId: string, value: string) {
  if (!value.trim()) return;
  try {
    const key = `${HISTORY_KEY_PREFIX}${dashboardId}:${filterId}`;
    const history = getSearchHistory(dashboardId, filterId);
    const updated = [value, ...history.filter((h) => h !== value)].slice(0, MAX_HISTORY);
    localStorage.setItem(key, JSON.stringify(updated));
  } catch {
    // ignore storage errors
  }
}

// ─── Individual Filter Controls ───────────────────────────────────────────────

/** Date filter with calendar picker */
const DateFilterControl = ({
  filter,
  value,
  onChange,
}: {
  filter: ConfiguredFilter;
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
}) => (
  <div className="flex items-center gap-1.5">
    <Label className="text-xs text-muted-foreground whitespace-nowrap">{filter.label}</Label>
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-7 gap-1.5 px-2 text-xs min-w-[120px] justify-start",
            !value && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="h-3 w-3" />
          {value ? format(value, "MMM d, yyyy") : "Select date"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={onChange} initialFocus />
      </PopoverContent>
    </Popover>
    {value && (
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5"
        onClick={() => onChange(undefined)}
      >
        <X className="h-3 w-3" />
      </Button>
    )}
  </div>
);

/** Number filter */
const NumberFilterControl = ({
  filter,
  value,
  onChange,
}: {
  filter: ConfiguredFilter;
  value: string;
  onChange: (val: string) => void;
}) => (
  <div className="flex items-center gap-1.5">
    <Label className="text-xs text-muted-foreground whitespace-nowrap">{filter.label}</Label>
    <Input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-7 w-24 text-xs"
      placeholder="Value"
    />
  </div>
);

/** Text filter */
const TextFilterControl = ({
  filter,
  value,
  onChange,
}: {
  filter: ConfiguredFilter;
  value: string;
  onChange: (val: string) => void;
}) => (
  <div className="flex items-center gap-1.5">
    <Label className="text-xs text-muted-foreground whitespace-nowrap">{filter.label}</Label>
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-7 w-32 text-xs"
      placeholder={`Filter ${filter.label.toLowerCase()}`}
    />
  </div>
);

/** Single Select with autocomplete search + search history */
const SingleSelectControl = ({
  filter,
  value,
  onChange,
  dashboardId,
}: {
  filter: ConfiguredFilter;
  value: string;
  onChange: (val: string) => void;
  dashboardId: string;
}) => {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const history = useMemo(
    () => getSearchHistory(dashboardId, filter.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dashboardId, filter.id, open]
  );

  const fetchOptions = useCallback(
    async (query: string) => {
      setLoading(true);
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const headers = await buildEdgeFunctionHeaders();
        const sql = `SELECT DISTINCT \`${filter.fieldName}\` FROM \`${filter.tableName}\` WHERE \`${filter.fieldName}\` IS NOT NULL AND CAST(\`${filter.fieldName}\` AS CHAR) LIKE '%${query.replace(/'/g, "''")}%' ORDER BY \`${filter.fieldName}\` LIMIT 50`;
        const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/execute/queries`, {
          method: "POST",
          headers,
          body: JSON.stringify([sql]),
        });
        if (res.ok) {
          const data = await res.json();
          let rows: any[] = [];
          if (Array.isArray(data)) {
            const first = data[0];
            rows = Array.isArray(first) ? first : first?.rows || first?.result || data;
          } else if (data?.results) {
            const first = Array.isArray(data.results) ? data.results[0] : data.results;
            rows = Array.isArray(first) ? first : first?.rows || first?.result || [];
          }
          // Use Object.values to extract the single selected column value,
          // avoiding case-sensitivity issues with column name keys
          setOptions(
            rows
              .map((r: any) => {
                const val = Object.values(r)[0];
                return val !== null && val !== undefined ? String(val) : "";
              })
              .filter(Boolean),
          );
        }
      } catch {
        // ignore fetch errors for autocomplete
      } finally {
        setLoading(false);
      }
    },
    [filter.fieldName, filter.tableName]
  );

  useEffect(() => {
    if (!open) return;
    fetchOptions(searchInput);
  }, [open]);

  const handleSearchChange = (q: string) => {
    setSearchInput(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(q), 300);
  };

  const handleSelect = (val: string) => {
    onChange(val);
    addToSearchHistory(dashboardId, filter.id, val);
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-1.5">
      <Label className="text-xs text-muted-foreground whitespace-nowrap">{filter.label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "h-7 w-40 justify-between text-xs",
              !value && "text-muted-foreground"
            )}
          >
            <span className="truncate">{value || "Select..."}</span>
            <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-0" align="start">
          <Command>
            <CommandInput
              placeholder={`Search ${filter.label.toLowerCase()}...`}
              value={searchInput}
              onValueChange={handleSearchChange}
              className="h-8 text-xs"
            />
            <CommandList>
              {loading && (
                <div className="flex items-center justify-center py-4 text-xs text-muted-foreground">
                  Loading…
                </div>
              )}
              {!loading && options.length === 0 && history.length === 0 && (
                <CommandEmpty className="text-xs">No results found.</CommandEmpty>
              )}
              {history.length > 0 && (
                <CommandGroup heading="Recent">
                  {history
                    .filter((h) => !searchInput || h.toLowerCase().includes(searchInput.toLowerCase()))
                    .slice(0, 5)
                    .map((h) => (
                      <CommandItem
                        key={`history:${h}`}
                        value={h}
                        onSelect={() => handleSelect(h)}
                        className="text-xs"
                      >
                        <History className="mr-2 h-3 w-3 text-muted-foreground" />
                        <span className="truncate">{h}</span>
                        {value === h && <Check className="ml-auto h-3 w-3" />}
                      </CommandItem>
                    ))}
                </CommandGroup>
              )}
              {options.length > 0 && (
                <>
                  {history.length > 0 && <CommandSeparator />}
                  <CommandGroup heading="Options">
                    {options.map((opt) => (
                      <CommandItem
                        key={opt}
                        value={opt}
                        onSelect={() => handleSelect(opt)}
                        className="text-xs"
                      >
                        <span className="truncate">{opt}</span>
                        {value === opt && <Check className="ml-auto h-3 w-3" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && (
        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => onChange("")}>
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
};

/** Multi Select with autocomplete search + search history */
const MultiSelectControl = ({
  filter,
  values,
  onChange,
  dashboardId,
}: {
  filter: ConfiguredFilter;
  values: string[];
  onChange: (vals: string[]) => void;
  dashboardId: string;
}) => {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const history = useMemo(
    () => getSearchHistory(dashboardId, filter.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dashboardId, filter.id, open]
  );

  const fetchOptions = useCallback(
    async (query: string) => {
      setLoading(true);
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const headers = await buildEdgeFunctionHeaders();
        const sql = `SELECT DISTINCT \`${filter.fieldName}\` FROM \`${filter.tableName}\` WHERE \`${filter.fieldName}\` IS NOT NULL AND CAST(\`${filter.fieldName}\` AS CHAR) LIKE '%${query.replace(/'/g, "''")}%' ORDER BY \`${filter.fieldName}\` LIMIT 50`;
        const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/execute/queries`, {
          method: "POST",
          headers,
          body: JSON.stringify([sql]),
        });
        if (res.ok) {
          const data = await res.json();
          let rows: any[] = [];
          if (Array.isArray(data)) {
            const first = data[0];
            rows = Array.isArray(first) ? first : first?.rows || first?.result || data;
          } else if (data?.results) {
            const first = Array.isArray(data.results) ? data.results[0] : data.results;
            rows = Array.isArray(first) ? first : first?.rows || first?.result || [];
          }
          // Use Object.values to extract the single selected column value,
          // avoiding case-sensitivity issues with column name keys
          setOptions(
            rows
              .map((r: any) => {
                const val = Object.values(r)[0];
                return val !== null && val !== undefined ? String(val) : "";
              })
              .filter(Boolean),
          );
        }
      } catch {
        // ignore fetch errors
      } finally {
        setLoading(false);
      }
    },
    [filter.fieldName, filter.tableName]
  );

  useEffect(() => {
    if (!open) return;
    fetchOptions(searchInput);
  }, [open]);

  const handleSearchChange = (q: string) => {
    setSearchInput(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(q), 300);
  };

  const toggleValue = (val: string) => {
    const next = values.includes(val) ? values.filter((v) => v !== val) : [...values, val];
    onChange(next);
    addToSearchHistory(dashboardId, filter.id, val);
  };

  return (
    <div className="flex items-center gap-1.5">
      <Label className="text-xs text-muted-foreground whitespace-nowrap">{filter.label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "h-7 min-w-[140px] max-w-[240px] justify-between text-xs",
              values.length === 0 && "text-muted-foreground"
            )}
          >
            <span className="truncate">
              {values.length === 0
                ? "Select..."
                : values.length === 1
                  ? values[0]
                  : `${values.length} selected`}
            </span>
            <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-0" align="start">
          <Command>
            <CommandInput
              placeholder={`Search ${filter.label.toLowerCase()}...`}
              value={searchInput}
              onValueChange={handleSearchChange}
              className="h-8 text-xs"
            />
            <CommandList>
              {loading && (
                <div className="flex items-center justify-center py-4 text-xs text-muted-foreground">
                  Loading…
                </div>
              )}
              {!loading && options.length === 0 && history.length === 0 && (
                <CommandEmpty className="text-xs">No results found.</CommandEmpty>
              )}
              {history.length > 0 && (
                <CommandGroup heading="Recent">
                  {history
                    .filter((h) => !searchInput || h.toLowerCase().includes(searchInput.toLowerCase()))
                    .slice(0, 5)
                    .map((h) => (
                      <CommandItem
                        key={`history:${h}`}
                        value={h}
                        onSelect={() => toggleValue(h)}
                        className="text-xs"
                      >
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                            values.includes(h)
                              ? "bg-primary text-primary-foreground"
                              : "opacity-50"
                          )}
                        >
                          {values.includes(h) && <Check className="h-3 w-3" />}
                        </div>
                        <History className="mr-1 h-3 w-3 text-muted-foreground" />
                        <span className="truncate">{h}</span>
                      </CommandItem>
                    ))}
                </CommandGroup>
              )}
              {options.length > 0 && (
                <>
                  {history.length > 0 && <CommandSeparator />}
                  <CommandGroup heading="Options">
                    {options.map((opt) => (
                      <CommandItem
                        key={opt}
                        value={opt}
                        onSelect={() => toggleValue(opt)}
                        className="text-xs"
                      >
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                            values.includes(opt)
                              ? "bg-primary text-primary-foreground"
                              : "opacity-50"
                          )}
                        >
                          {values.includes(opt) && <Check className="h-3 w-3" />}
                        </div>
                        <span className="truncate">{opt}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {values.length > 0 && (
        <div className="flex items-center gap-0.5">
          {values.slice(0, 2).map((v) => (
            <Badge key={v} variant="secondary" className="text-[10px] h-5 gap-0.5">
              {v.length > 12 ? `${v.slice(0, 12)}...` : v}
              <button
                onClick={() => onChange(values.filter((x) => x !== v))}
                className="ml-0.5 hover:text-destructive"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </Badge>
          ))}
          {values.length > 2 && (
            <Badge variant="outline" className="text-[10px] h-5">
              +{values.length - 2}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const DashboardFilterBar = ({
  filters,
  dashboardId,
  onChange,
}: DashboardFilterBarProps) => {
  const [values, setValues] = useState<Record<string, unknown>>({});

  const updateValue = useCallback(
    (filterId: string, value: unknown) => {
      setValues((prev) => {
        const next = { ...prev, [filterId]: value };
        // Defer onChange to next tick to avoid state sync issues
        setTimeout(() => onChange(next), 0);
        return next;
      });
    },
    [onChange]
  );

  if (filters.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-4 py-2">
      <Filter className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />

      {filters.map((filter) => {
        switch (filter.filterType) {
          case "date":
            return (
              <DateFilterControl
                key={filter.id}
                filter={filter}
                value={values[filter.id] as Date | undefined}
                onChange={(d) => updateValue(filter.id, d)}
              />
            );
          case "number":
            return (
              <NumberFilterControl
                key={filter.id}
                filter={filter}
                value={(values[filter.id] as string) ?? ""}
                onChange={(v) => updateValue(filter.id, v)}
              />
            );
          case "text":
            return (
              <TextFilterControl
                key={filter.id}
                filter={filter}
                value={(values[filter.id] as string) ?? ""}
                onChange={(v) => updateValue(filter.id, v)}
              />
            );
          case "singleselect":
            return (
              <SingleSelectControl
                key={filter.id}
                filter={filter}
                value={(values[filter.id] as string) ?? ""}
                onChange={(v) => updateValue(filter.id, v)}
                dashboardId={dashboardId}
              />
            );
          case "multiselect":
            return (
              <MultiSelectControl
                key={filter.id}
                filter={filter}
                values={(values[filter.id] as string[]) ?? []}
                onChange={(v) => updateValue(filter.id, v)}
                dashboardId={dashboardId}
              />
            );
          default:
            return null;
        }
      })}

      {Object.values(values).some((v) =>
        Array.isArray(v) ? v.length > 0 : v !== undefined && v !== ""
      ) && (
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => {
            setValues({});
            onChange({});
          }}
        >
          <X className="h-3 w-3" />
          Clear all
        </Button>
      )}
    </div>
  );
};

export default DashboardFilterBar;
